import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { DepartmentsCard } from "@/components/DepartmentsCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Loader2, Save, Plus, Pencil, Trash2, Star, Download,
} from "lucide-react";
import { crDate, currentMonthISO } from "@/lib/dateFormat";

// El mes en curso SIEMPRE en hora de Costa Rica (no la del navegador).
const today = () => currentMonthISO();

const fmtMoney = (n, ccy = "USD") => {
  const symbol = ccy === "CRC" ? "₡" : "$";
  return `${symbol}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function InvoiceSettings() {
  const { t } = useI18n();
  const [tab, setTab] = useState("issuers");

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("invoice_settings")}
        subtitle={t("invoice_settings_subtitle")}
        testId="invoice-settings-header"
      />

      <DepartmentsCard />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 h-auto p-1 flex flex-wrap" data-testid="invoice-settings-tabs">
          <TabsTrigger value="issuers" data-testid="tab-issuers">{t("tab_issuers")}</TabsTrigger>
          <TabsTrigger value="shared" data-testid="tab-shared">{t("tab_shared")}</TabsTrigger>
          <TabsTrigger value="assignments" data-testid="tab-assignments">{t("tab_assignments")}</TabsTrigger>
          <TabsTrigger value="report" data-testid="tab-report">{t("tab_report")}</TabsTrigger>
        </TabsList>

        <TabsContent value="issuers" className="mt-6"><IssuersTab /></TabsContent>
        <TabsContent value="shared" className="mt-6"><SharedTab /></TabsContent>
        <TabsContent value="assignments" className="mt-6"><AssignmentsTab /></TabsContent>
        <TabsContent value="report" className="mt-6"><ReportTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- Issuers Tab ---------------- */
function IssuersTab() {
  const { t } = useI18n();
  const [issuers, setIssuers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | issuer | "new"
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get("/issuers");
      setIssuers(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onSaved = () => { setEditing(null); load(); };

  const setDefault = async (iss) => {
    try {
      await api.put(`/issuers/${iss.id}`, { is_default: true });
      toast.success(t("saved"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/issuers/${deleting.id}`);
      toast.success(t("deleted"));
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("issuer_in_use"));
      setDeleting(null);
    }
  };

  if (loading) return <Loading />;

  return (
    <section className="space-y-4" data-testid="issuers-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <p className="text-sm text-slate-500 max-w-2xl">{t("issuers_subtitle")}</p>
        <Button
          onClick={() => setEditing("new")}
          data-testid="add-issuer-btn"
          className="btn-brand"
        >
          <Plus className="h-4 w-4 mr-1" /> {t("add_issuer")}
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("name")}</TableHead>
              <TableHead>{t("issuer_tax_id")}</TableHead>
              <TableHead>{t("prefix")}</TableHead>
              <TableHead className="text-right">{t("next_invoice_number")}</TableHead>
              <TableHead></TableHead>
              <TableHead className="text-right">{t("edit")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {issuers.map((iss) => (
              <TableRow key={iss.id} data-testid={`issuer-row-${iss.prefix || iss.id}`}>
                <TableCell className="font-medium">{iss.name}</TableCell>
                <TableCell className="text-slate-600">{iss.tax_id}</TableCell>
                <TableCell><Badge variant="secondary">{iss.prefix || "—"}</Badge></TableCell>
                <TableCell className="text-right tabular-nums">{iss.next_number}</TableCell>
                <TableCell>
                  {iss.is_default ? (
                    <Badge className="bg-amber-100 text-amber-800 border-amber-200 border" data-testid={`default-badge-${iss.prefix}`}>
                      <Star className="h-3 w-3 mr-1 inline" /> {t("default_issuer")}
                    </Badge>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setDefault(iss)}
                      data-testid={`set-default-${iss.prefix}`}
                    >
                      <Star className="h-3 w-3 mr-1" /> {t("set_default")}
                    </Button>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setEditing(iss)}
                      data-testid={`edit-issuer-${iss.prefix}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      disabled={iss.is_default}
                      onClick={() => setDeleting(iss)}
                      data-testid={`delete-issuer-${iss.prefix}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {editing && (
        <IssuerDialog
          issuer={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirm_delete")}</AlertDialogTitle>
            <AlertDialogDescription>{t("confirm_delete_issuer")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-issuer">
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function IssuerDialog({ issuer, onClose, onSaved }) {
  const { t } = useI18n();
  const isNew = !issuer;
  const [form, setForm] = useState({
    name: issuer?.name || "",
    tax_id: issuer?.tax_id || "",
    prefix: issuer?.prefix || "",
    next_number: issuer?.next_number || 1,
    is_default: issuer?.is_default || false,
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name?.trim() || !form.tax_id?.trim()) {
      toast.error(t("error_generic"));
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/issuers", {
          name: form.name.trim(),
          tax_id: form.tax_id.trim(),
          prefix: (form.prefix || "").trim(),
          next_number: Number(form.next_number) || 1,
          is_default: !!form.is_default,
        });
      } else {
        await api.put(`/issuers/${issuer.id}`, {
          name: form.name.trim(),
          tax_id: form.tax_id.trim(),
          prefix: (form.prefix || "").trim(),
          next_number: Number(form.next_number) || 1,
          is_default: !!form.is_default,
        });
      }
      toast.success(t("saved"));
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="issuer-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? t("new_issuer") : t("edit_issuer")}</DialogTitle>
          <DialogDescription>{t("issuer_block_hint")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">{t("issuer_name")}</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="dialog-issuer-name"
            />
          </div>
          <div>
            <Label className="text-xs">{t("issuer_tax_id")}</Label>
            <Input
              value={form.tax_id}
              onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
              data-testid="dialog-issuer-tax-id"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">{t("prefix")}</Label>
              <Input
                value={form.prefix}
                onChange={(e) => setForm({ ...form, prefix: e.target.value.toUpperCase() })}
                placeholder="WF"
                data-testid="dialog-issuer-prefix"
              />
              <p className="text-xs text-slate-500 mt-1">{t("prefix_hint")}</p>
            </div>
            <div>
              <Label className="text-xs">{t("next_invoice_number")}</Label>
              <Input
                type="number"
                min={1}
                value={form.next_number}
                onChange={(e) => setForm({ ...form, next_number: e.target.value })}
                data-testid="dialog-issuer-next-number"
              />
              {!isNew && (
                <p className="text-[11px] text-amber-700 mt-1">⚠️ {t("next_number_warning")}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={!!form.is_default}
              onCheckedChange={(v) => setForm({ ...form, is_default: !!v })}
              id="is-default"
              data-testid="dialog-issuer-default"
            />
            <Label htmlFor="is-default" className="cursor-pointer text-sm">
              {t("default_issuer")}
            </Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
          <Button onClick={submit} disabled={saving} className="btn-brand" data-testid="save-issuer-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
            {t("save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Shared Tab ---------------- */
function SharedTab() {
  const { t } = useI18n();
  const [shared, setShared] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get("/invoices/shared-settings");
      setShared(r.data || { location: "", policy_url: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/invoices/shared-settings", {
        location: shared.location || "",
        policy_url: shared.policy_url || "",
      });
      toast.success(t("saved"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !shared) return <Loading />;

  return (
    <section
      className="bg-white border border-slate-200 rounded-xl p-6 space-y-5 max-w-3xl"
      data-testid="shared-tab"
    >
      <p className="text-sm text-slate-500">{t("shared_subtitle")}</p>
      <div className="space-y-4">
        <div>
          <Label className="text-xs">{t("issuer_location")}</Label>
          <Input
            value={shared.location || ""}
            onChange={(e) => setShared({ ...shared, location: e.target.value })}
            placeholder="Santa Ana, Costa Rica"
            data-testid="shared-location"
          />
        </div>
        <div>
          <Label className="text-xs">{t("policy_url")}</Label>
          <Input
            type="url"
            value={shared.policy_url || ""}
            onChange={(e) => setShared({ ...shared, policy_url: e.target.value })}
            placeholder="https://wespeakspanish-cr.com/about-us/policies-conditions/"
            data-testid="shared-policy-url"
          />
          <p className="text-xs text-slate-500 mt-1">{t("policy_url_hint")}</p>
        </div>
      </div>
      <div>
        <Button onClick={save} disabled={saving} className="btn-brand" data-testid="save-shared-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
          {t("save")}
        </Button>
      </div>
    </section>
  );
}

/* ---------------- Assignments Tab ---------------- */
function AssignmentsTab() {
  const { t } = useI18n();
  const [students, setStudents] = useState([]);
  const [issuers, setIssuers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkIssuer, setBulkIssuer] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      const [u, i] = await Promise.all([
        api.get("/users", { params: { role: "student" } }),
        api.get("/issuers"),
      ]);
      setStudents(u.data || []);
      setIssuers(i.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const defaultIssuer = useMemo(() => issuers.find((i) => i.is_default), [issuers]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) =>
      (s.name || "").toLowerCase().includes(q) ||
      (s.email || "").toLowerCase().includes(q)
    );
  }, [students, filter]);

  const assignOne = async (studentId, issuerId) => {
    try {
      // Empty issuerId -> revert to default by unassigning to current and assigning to default
      const target = issuerId || (defaultIssuer && defaultIssuer.id);
      if (!target) return;
      await api.post(`/issuers/${target}/assign`, { student_ids: [studentId] });
      // Optimistic update
      setStudents((prev) =>
        prev.map((s) => (s.user_id === studentId ? { ...s, issuer_id: issuerId || null } : s))
      );
      toast.success(t("saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    }
  };

  const applyBulk = async () => {
    if (!bulkIssuer || selected.size === 0) return;
    try {
      const ids = Array.from(selected);
      await api.post(`/issuers/${bulkIssuer}/assign`, { student_ids: ids });
      setStudents((prev) =>
        prev.map((s) => (selected.has(s.user_id) ? { ...s, issuer_id: bulkIssuer } : s))
      );
      setSelected(new Set());
      setBulkIssuer("");
      toast.success(t("saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    }
  };

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((s) => s.user_id)));
    }
  };

  if (loading) return <Loading />;

  return (
    <section className="space-y-4" data-testid="assignments-tab">
      <p className="text-sm text-slate-500 max-w-3xl">{t("assignments_subtitle")}</p>

      <div className="flex flex-wrap gap-3 items-center justify-between">
        <Input
          placeholder={t("filter_students")}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="max-w-sm"
          data-testid="filter-students"
        />
        {selected.size > 0 && (
          <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
            <span className="text-xs text-slate-600">
              {selected.size} {t("students_selected")}
            </span>
            <span className="text-xs text-slate-500">{t("bulk_assign")}</span>
            <Select value={bulkIssuer} onValueChange={setBulkIssuer}>
              <SelectTrigger className="h-8 w-40" data-testid="bulk-issuer-select">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                {issuers.map((iss) => (
                  <SelectItem key={iss.id} value={iss.id}>
                    {iss.prefix ? `${iss.prefix} · ${iss.name}` : iss.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              onClick={applyBulk}
              disabled={!bulkIssuer}
              data-testid="apply-bulk-assign"
            >
              {t("apply_bulk_assign")}
            </Button>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={selected.size > 0 && selected.size === filtered.length}
                  onCheckedChange={toggleAll}
                  data-testid="select-all-students"
                />
              </TableHead>
              <TableHead>{t("student")}</TableHead>
              <TableHead>{t("email")}</TableHead>
              <TableHead>{t("assigned_issuer")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((s) => (
              <TableRow key={s.user_id} data-testid={`assign-row-${s.user_id}`}>
                <TableCell>
                  <Checkbox
                    checked={selected.has(s.user_id)}
                    onCheckedChange={() => toggle(s.user_id)}
                  />
                </TableCell>
                <TableCell className="font-medium">{s.name || "—"}</TableCell>
                <TableCell className="text-slate-500 text-sm">{s.email}</TableCell>
                <TableCell>
                  <Select
                    value={s.issuer_id || "__default__"}
                    onValueChange={(v) => assignOne(s.user_id, v === "__default__" ? "" : v)}
                  >
                    <SelectTrigger className="h-8 w-56" data-testid={`assign-select-${s.user_id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__default__">
                        ⭐ {t("use_default")}
                        {defaultIssuer ? ` (${defaultIssuer.prefix || defaultIssuer.name})` : ""}
                      </SelectItem>
                      {issuers.map((iss) => (
                        <SelectItem key={iss.id} value={iss.id}>
                          {iss.prefix ? `${iss.prefix} · ${iss.name}` : iss.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}

/* ---------------- Monthly Report Tab ---------------- */
function ReportTab() {
  const { t } = useI18n();
  const [month, setMonth] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (m) => {
    try {
      setLoading(true);
      const r = await api.get("/invoices/report", { params: { month: m } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(month); }, [month]);

  const dl = async (enrollmentId, invoiceNumber) => {
    // Opens the invoice PDF inline in a new tab. User can download from the
    // browser's PDF viewer if they want (no auto-download).
    try {
      const r = await api.get(`/enrollments/${enrollmentId}/invoice`, {
        params: { lang: (localStorage.getItem("lang") || "es") },
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    }
  };

  return (
    <section className="space-y-4" data-testid="report-tab">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <p className="text-sm text-slate-500 max-w-3xl">{t("report_subtitle")}</p>
        <div>
          <Label className="text-xs">{t("month")}</Label>
          <Input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="w-44"
            data-testid="report-month"
          />
        </div>
      </div>

      {loading ? (
        <Loading />
      ) : !data ? null : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <KPI label={t("total_invoices")} value={data.total_count} testId="kpi-total-count" />
            <KPI
              label={t("total_amount")}
              value={fmtMoney(data.total_amount, "USD")}
              testId="kpi-total-amount"
            />
            <KPI
              label={t("by_issuer")}
              value={(data.by_issuer || []).length}
              testId="kpi-issuers-count"
            />
          </div>

          {(data.by_issuer || []).length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 uppercase tracking-wider">
                {t("by_issuer")}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {data.by_issuer.map((b) => (
                  <div
                    key={b.issuer_id}
                    className="border border-slate-200 rounded-lg p-4 bg-slate-50"
                    data-testid={`report-issuer-${b.issuer_id}`}
                  >
                    <div className="text-xs text-slate-500">{b.issuer_name}</div>
                    <div className="font-display text-2xl font-bold text-slate-900 mt-1">
                      {b.count}
                    </div>
                    <div className="text-sm text-slate-600">{fmtMoney(b.amount, "USD")}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            {(data.invoices || []).length === 0 ? (
              <div className="p-10 text-center text-slate-500" data-testid="no-invoices">
                {t("no_invoices_month")}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("issued_at")}</TableHead>
                    <TableHead>{t("invoice_number")}</TableHead>
                    <TableHead>{t("student")}</TableHead>
                    <TableHead>{t("issuer_name")}</TableHead>
                    <TableHead className="text-right">{t("amount")}</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.invoices.map((inv) => (
                    <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                      <TableCell className="text-sm text-slate-600">
                        {crDate(inv.issued_at)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="font-mono">
                          {inv.display_number || inv.invoice_number}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">{inv.student_name || "—"}</TableCell>
                      <TableCell className="text-sm text-slate-600">{inv.issuer_name}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {fmtMoney(inv.amount, inv.currency)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => dl(inv.enrollment_id, inv.display_number || inv.invoice_number)}
                          data-testid={`download-invoice-${inv.id}`}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </>
      )}
    </section>
  );
}

/* ---------------- Helpers ---------------- */
function KPI({ label, value, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testId}>
      <div className="text-xs text-slate-500 uppercase tracking-wider">{label}</div>
      <div className="font-display text-3xl font-bold text-slate-900 mt-2">{value}</div>
    </div>
  );
}

function Loading() {
  const { t } = useI18n();
  return (
    <div className="p-10 flex items-center justify-center text-slate-500">
      <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t("loading")}
    </div>
  );
}
